// rules.jsx
import React, { useCallback, useEffect, useState } from 'react';
import '../fonts/breuer-headline.css';
import './rules.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginUser } from '../loginSlice';
import { initQuestions } from '../../functions/setUserQuestions';
import { selectAdminToken } from '../../admin/sessionSlice';
import { processQuestions } from '../../admin/questionSlice';
import { setBackButtonUrl } from '../uiSlice';
import { resolveGameEntryRoute } from '../../functions/stageReportSync';
import logoImg from './img/logo.png';
import bgImg from './img/rules-bg.jpg';
import bgImgMobile from './img/rules-bg-mobile.png';

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

/** Mirrors the other pages' mobile-portrait breakpoint, used to pick background-mobile.png. */
function useMobilePortrait() {
  const [isMobilePortrait, setIsMobilePortrait] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_PORTRAIT_MQ);
    const sync = () => setIsMobilePortrait(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  return isMobilePortrait;
}

/** Mirrors old/rules1-4.php's shared right-click/devtools-shortcut block. */
function useDisableInspect() {
  useEffect(() => {
    const blockContextMenu = (event) => event.preventDefault();
    const blockShortcutKeys = (e) => {
      if (e.keyCode === 123) return false; // F12
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) return false; // Ctrl+Shift+I
      if (e.ctrlKey && e.shiftKey && e.keyCode === 74) return false; // Ctrl+Shift+J
      if (e.ctrlKey && e.keyCode === 85) return false; // Ctrl+U
      return true;
    };
    document.addEventListener('contextmenu', blockContextMenu);
    document.addEventListener('keydown', blockShortcutKeys);
    return () => {
      document.removeEventListener('contextmenu', blockContextMenu);
      document.removeEventListener('keydown', blockShortcutKeys);
    };
  }, []);
}

function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData } = useSelector((state) => state.theme);
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const isMobilePortrait = useMobilePortrait();
  const [isLoading, setIsLoading] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);
  // old/rules.php (story) -> old/rules1.php (bulleted rules) -> old/room.php.
  const [step, setStep] = useState(1);

  useDisableInspect();

  // A hard refresh on /rules loses the redux user/theme state (it's only
  // populated by Login's own mount) — rehydrate from the persisted session
  // the same way Login does, so this page doesn't spin forever.
  useEffect(() => {
    if (!user) dispatch(loginUser());
  }, [user, dispatch]);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    if (!themeData || !user || !adminToken) return;

    // Welcome / full-house themes have no quiz config (no noOfQuestion) — skip setUserQuestions.
    const baseQuestions = parseInt(
      themeData.noOfQuestion ?? themeData.no_of_questions,
      10
    );
    const needsQuizInit = Number.isFinite(baseQuestions) && baseQuestions >= 1;

    setIsLoading(true);

    if (!needsQuizInit) {
      return;
    }

    dispatch(processQuestions())
      .unwrap()
      .then(() => {
        return initQuestions({
          userId: user.userId,
          email: user.email,
          fullName: user.name,
          themeName: themeData.themename,
          token: adminToken,
        });
      })
      .catch((err) => {
        console.error('Error in process→init chain:', err);
      });
  }, [themeData, user, adminToken, dispatch]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [dispatch]);

  // Page-scoped html/body overrides (no horizontal scroll; vertical scroll
  // stays on the body for short screens — see rules.css).
  useEffect(() => {
    document.documentElement.classList.add("rules-lock");
    return () => {
      document.documentElement.classList.remove("rules-lock");
    };
  }, []);

  // Paint the background on <body> (not a page div) so it extends behind the
  // shared header too, instead of leaving the old theme background showing
  // through there while a separate div covers the rest of the page.
  useEffect(() => {
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };
    body.classList.remove("common-bg");
    body.style.backgroundImage = `url("${isMobilePortrait ? bgImgMobile : bgImg}")`;
    body.style.backgroundSize = "cover";
    body.style.backgroundRepeat = "no-repeat";
    body.style.backgroundPosition = "center center";
    body.style.backgroundAttachment = "fixed";
    return () => {
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, [isMobilePortrait]);

  const goToGame = useCallback(async () => {
    if (!user || nextLoading) return;

    let storedUser = user;
    try {
      storedUser = JSON.parse(sessionStorage.getItem("userData") || "{}");
    } catch {
      storedUser = user;
    }

    setNextLoading(true);
    try {
      const route = await resolveGameEntryRoute({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass: storedUser.sessionId === "demobypass",
      });
      navigate(route, { replace: true });
    } catch (err) {
      console.error("rules goToGame:", err);
      navigate("/game", { replace: true });
    } finally {
      setNextLoading(false);
    }
  }, [user, nextLoading, backendBase, adminToken, navigate]);

  const handleNextClick = (e) => {
    e.preventDefault();
    if (step < 2) {
      setStep((s) => s + 1);
      return;
    }
    goToGame();
  };

  if (!isLoading) {
    return (
      <div className='quiz-loader-container'>
        <div className="quiz-loader"></div>
      </div>
    );
  }

  return (
    <div className="user-rules-page">
      <div className="user-rules-stack">
        {step === 1 && (
          <div className="rules-story-text">
            <h4>
              You find yourself trapped in a mysterious, dimly lit room —
              your own Mind Palace. The only way out is by solving self-care
              and self-love puzzles that help you unlock the doors of
              stress, overthinking, and burnout.
            </h4>
            <h4>
              Each puzzle reveals a &lsquo;Key to Well-being&rsquo; that
              brings you closer to ultimate self-care freedom.
            </h4>
          </div>
        )}

        {step === 2 && (
          <>
            <img src={logoImg} className="rules-center-logo" alt="Escape To Inner Peace" />
            <h3 className="rules-pill-heading">Rules</h3>
            <ul className="rule-list">
              <li>You are trapped in a room which is a mind palace</li>
              <li>
                Clues will appear as you progress. Navigate through the
                items in the Room
              </li>
              <li>Think smart. Think fast. Think together</li>
             
            </ul>
          </>
        )}

        <button
          type="button"
          className="user-next-button"
          onClick={handleNextClick}
          disabled={step === 2 && nextLoading}
        >
          {step === 2 && nextLoading ? "Loading…" : "Next"}
        </button>
      </div>
    </div>
  );
}

export default UserRules;
